import React, { useContext } from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Pagination } from "@design-system/Pagination/Pagination";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../../../transactions/PaginatedTransactionsTable.context";
import {
    SelectedTransactionsCountControl,
    SelectedTransactionsFieldUpdateControl,
} from "../SelectedTransactionsUpdateControl";
import { RuleGroupSelect } from "./RuleGroupSelect";
import { useActionsContext } from "./ActionsContext";
import { useApplyActions } from "./useApplyActions";
import styles from "./ActionsCard.module.scss";

const APPLY_PENDING_ACTIONS_TOOLTIP =
    "Apply pending condition-based actions to the transactions list before confirming.";

interface Props {
    onConfirm: () => void;
    isSubmitting: boolean;
    confirmDisabled: boolean;
    confirmDisabledTooltip?: string;
}

export const ActionsCardFooter: React.FC<Props> = ({
    onConfirm,
    isSubmitting,
    confirmDisabled,
    confirmDisabledTooltip,
}) => {
    const { ruleGroupId, setRuleGroupId } = useActionsContext();
    const { currentPage, totalPages } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setPage } = useContext(PaginatedTransactionsActionsContext);
    const {
        applyActions,
        isApplying,
        isButtonVisible,
        isButtonDisabled,
        tooltip,
    } = useApplyActions();
    const shouldBlockConfirmForApplyActions =
        isButtonVisible && !isButtonDisabled;
    const isConfirmDisabled =
        confirmDisabled || shouldBlockConfirmForApplyActions;
    let confirmTooltip: string | undefined;
    if (confirmDisabled && confirmDisabledTooltip) {
        confirmTooltip = confirmDisabledTooltip;
    } else if (shouldBlockConfirmForApplyActions) {
        confirmTooltip = APPLY_PENDING_ACTIONS_TOOLTIP;
    }

    return (
        <div className={styles.cardFooter}>
            <div className={styles.footerLeft}>
                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        pageCount={totalPages}
                        onPageChange={setPage}
                        size="md"
                    />
                )}
                <SelectedTransactionsCountControl />
            </div>
            <div className={styles.footerRight}>
                <SelectedTransactionsFieldUpdateControl />
                <RuleGroupSelect
                    value={ruleGroupId}
                    onChange={setRuleGroupId}
                />
                {isButtonVisible && (
                    <Tooltip content={tooltip}>
                        <span className={styles.applyActionsTooltipTrigger}>
                            <ButtonWithLoader
                                variant="primary"
                                size="md"
                                onClick={applyActions}
                                loading={isApplying}
                                disabled={isButtonDisabled}
                            >
                                Apply actions
                            </ButtonWithLoader>
                        </span>
                    </Tooltip>
                )}
                <ButtonWithLoader
                    variant="primary"
                    size="md"
                    onClick={onConfirm}
                    loading={isSubmitting}
                    disabled={isConfirmDisabled}
                    tooltip={confirmTooltip}
                    hotKey="mod+enter"
                    hotKeyOptions={{
                        enabled: !isSubmitting && !isConfirmDisabled,
                        description: "Confirm actions and create rules",
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    Confirm
                </ButtonWithLoader>
            </div>
        </div>
    );
};
